import {
    getDealTabLabel,
    UIDealDetailsTabs,
    useClientSettingStore,
    useStore,
    useStoreUI
} from '@assets/model/Store';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { useLayoutEffect, useRef, useState } from 'react';
import Offcanvas from 'react-bootstrap/Offcanvas';
import { Flex } from '../UI/Flex/Flex';
import * as style from './DealViewSidebar.module.less';
import { GetIcon } from '../Icons/Icons';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';

const SIDEBAR_TOOLTIP_ID = 'sidebar-nav-tooltip';
const SIDEBAR_RAIL_WIDTH = '48px';
const SIDEBAR_EXPANDED_WIDTH = '209px';

const applySidebarWidth = (expanded: boolean): void => {
    document.documentElement.style.setProperty(
        '--current-sidebar-width',
        expanded ? SIDEBAR_EXPANDED_WIDTH : SIDEBAR_RAIL_WIDTH
    );
};

type MenuEntry = { label: string; tabId: UIDealDetailsTabs };
interface CollapseableMenuProps {
    menuItems: MenuEntry[];
    isExpanded: boolean;
}

const CollapseableMenu = observer((p: CollapseableMenuProps) => {
    const uiStore = useStoreUI();
    const clientStore = useClientSettingStore();
    const getIconForMenuItem = (tabId: UIDealDetailsTabs): string => {
        switch (tabId) {
            case UIDealDetailsTabs.DASHBOARD:
                return GetIcon('menu_dashboard');
            case UIDealDetailsTabs.PROJECT_FILES:
                return GetIcon('menu_case_files');
            case UIDealDetailsTabs.WORKSTREAMS:
                return GetIcon('menu_workstreams');
            case UIDealDetailsTabs.OUTPUTS:
                return GetIcon('menu_outputs');
            case UIDealDetailsTabs.TASK_VIEW_DEBUG:
                return GetIcon('menu_flowise_debug');
            case UIDealDetailsTabs.DETAILS:
                return GetIcon('menu_deal_details');
            case UIDealDetailsTabs.SCORE_BREAKDOWN:
                return GetIcon('menu_score_details');
            case UIDealDetailsTabs.COMPARE:
                return GetIcon('menu_benchmark');
            case UIDealDetailsTabs.A1_NARRATIVE:
                return GetIcon('menu_narrative_a1');

            default:
                return GetIcon('menu_flowise_debug');
        }
    };

    const makeMenuItem = (tab: UIDealDetailsTabs, label: string) => {
        const isSelected = uiStore.dealViewTab === tab;
        const icon = getIconForMenuItem(tab);
        const selectTab = () => {
            uiStore.dealViewTab = tab;
            // Clicking Workstreams always returns to the overview, even when a
            // module is already open on that tab.
            if (tab === UIDealDetailsTabs.WORKSTREAMS) {
                uiStore.workstreamModuleTaskId = null;
            }
        };
        return (
            <div key={tab} className={style.menuItem}>
                <div
                    role="button"
                    tabIndex={0}
                    aria-label={label}
                    aria-current={isSelected ? 'page' : undefined}
                    onClick={selectTab}
                    onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            selectTab();
                        }
                    }}
                    data-tooltip-id={
                        p.isExpanded ? undefined : SIDEBAR_TOOLTIP_ID
                    }
                    data-tooltip-content={p.isExpanded ? undefined : label}
                    data-tooltip-place="right"
                    className={classNames(style.groupLink, {
                        [style.groupLinkExpanded]: p.isExpanded,
                        [style.groupLinkSelected]: isSelected
                    })}
                >
                    <span
                        className={style.iconMask}
                        style={{
                            maskImage: `url(${icon})`,
                            WebkitMaskImage: `url(${icon})`
                        }}
                    />
                    <span
                        className={classNames(style.label, {
                            [style.labelExpanded]: p.isExpanded
                        })}
                    >
                        {label}
                    </span>
                </div>
                <div
                    className={classNames(style.horizLine, {
                        [style.horizLineExpanded]: p.isExpanded
                    })}
                ></div>
            </div>
        );
    };

    const menuItems = p.menuItems.map(item =>
        makeMenuItem(item.tabId, item.label)
    );

    return <div className={style.menuList}>{menuItems}</div>;
});

const DealViewSidebar = observer(() => {
    const store = useStore();
    const clientStore = useClientSettingStore();
    // Always start collapsed when entering a deal — the expanded state is
    // intentionally not persisted across navigations/sessions.
    const [isExpanded, setIsExpanded] = useState(false);

    const isFirstRender = useRef(true);

    // Sync the layout variable with the current state. On the very first render
    // suppress transitions so the sidebar doesn't play a closing animation on
    // page load; re-enable them on the next frame once the initial (collapsed)
    // state has painted.
    useLayoutEffect(() => {
        const root = document.documentElement;
        if (isFirstRender.current) {
            root.classList.add('sidebar-no-transition');
            applySidebarWidth(isExpanded);
            isFirstRender.current = false;
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    root.classList.remove('sidebar-no-transition');
                });
            });
        } else {
            applySidebarWidth(isExpanded);
        }
        return () => applySidebarWidth(false);
    }, [isExpanded]);

    const toggleExpanded = () => {
        setIsExpanded(prev => !prev);
    };

    const do_we_have_pdf = store.query.report_has_pdf;
    const showOnlyNarrativeTabs =
        clientStore.settings.onOff?.showOnlyNarrativeAndWorkStream ?? false;

    const menuItems: MenuEntry[] = [];
    const addMenuItem = (tabId: UIDealDetailsTabs) => {
        menuItems.push({ tabId, label: getDealTabLabel(tabId) });
    };

    if (!showOnlyNarrativeTabs) {
        addMenuItem(UIDealDetailsTabs.DASHBOARD);
    }

    if (do_we_have_pdf && !showOnlyNarrativeTabs) {
        addMenuItem(UIDealDetailsTabs.DETAILS);
        addMenuItem(UIDealDetailsTabs.COMPARE);
        addMenuItem(UIDealDetailsTabs.SCORE_BREAKDOWN);
    }

    addMenuItem(UIDealDetailsTabs.WORKSTREAMS);

    if (clientStore.showA1NarrtiveTab) {
        addMenuItem(UIDealDetailsTabs.A1_NARRATIVE);
        addMenuItem(UIDealDetailsTabs.PROJECT_FILES);
    }
    if (clientStore.showOutputsTab) {
        addMenuItem(UIDealDetailsTabs.OUTPUTS);
    }

    // if (store.isDev) {
    //     aiConsultMenu.push({
    //         label: 'Task View Debug',
    //         tabId: UIDealDetailsTabs.TASK_VIEW_DEBUG
    //     });
    // }

    // if (store.isAdmin) {
    //     aiConsultMenu.push({
    //         label: 'Flowise Debug',
    //         tabId: UIDealDetailsTabs.FLOWISE
    //     });
    // }

    return (
        <>
            <Offcanvas
                show={true}
                enforceFocus={false}
                backdrop={false}
                scroll={true}
                className={classNames('sidebar-offcanvas', {
                    'sidebar-expanded': isExpanded
                })}
            >
                <Offcanvas.Body>
                    <div
                        className={classNames(style.toggleRow, {
                            [style.toggleRowExpanded]: isExpanded
                        })}
                    >
                        <button
                            type="button"
                            className={classNames(style.toggleButton, {
                                [style.toggleButtonExpanded]: isExpanded
                            })}
                            onClick={toggleExpanded}
                            aria-expanded={isExpanded}
                            aria-label={
                                isExpanded
                                    ? 'Collapse sidebar'
                                    : 'Expand sidebar'
                            }
                            data-tooltip-id={
                                isExpanded ? undefined : SIDEBAR_TOOLTIP_ID
                            }
                            data-tooltip-content={
                                isExpanded ? undefined : 'Open'
                            }
                            data-tooltip-place="right"
                        >
                            <i className="bi bi-layout-sidebar" />
                        </button>
                    </div>
                    <CollapseableMenu
                        isExpanded={isExpanded}
                        menuItems={menuItems}
                    />
                </Offcanvas.Body>
            </Offcanvas>
            {/*
             * Rendered outside the Offcanvas so it escapes the rail's
             * overflow:hidden and stacking context — otherwise the tooltip is
             * clipped at the sidebar's right edge. Anchors connect by
             * data-tooltip-id, so DOM position doesn't matter.
             */}
            {!isExpanded && (
                <Tooltip
                    id={SIDEBAR_TOOLTIP_ID}
                    delayShow={150}
                    positionStrategy="fixed"
                    className={style.navTooltip}
                    style={{
                        backgroundColor: '#1a2f4a',
                        color: '#ffffff',
                        borderRadius: '8px',
                        padding: '6px 12px',
                        fontSize: '14px',
                        fontWeight: 500,
                        fontFamily: 'Hanken Grotesk'
                    }}
                />
            )}
        </>
    );
});

export default DealViewSidebar;
